/**
 * District Analyser — State Agency role screen
 * FR-S05 to FR-S07
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import {
  gujaratDistrictAnalyser,
  gujaratStateDiscoms,
  gujaratAvgSpecificYield,
} from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

// ── Tile definitions ──────────────────────────────────────────────────────────
interface TileDef {
  label: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
  filterFn: (d: typeof gujaratDistrictAnalyser[0]) => boolean;
}

const TILES: TileDef[] = [
  {
    label: 'PR below 70%',
    textCls: 'text-red-600', bgCls: 'bg-red-50', ringCls: 'ring-red-500',
    filterFn: d => d.prPercent < 70,
  },
  {
    label: 'Generation dropping MoM',
    textCls: 'text-amber-600', bgCls: 'bg-amber-50', ringCls: 'ring-amber-500',
    filterFn: d => d.momTrend < 0,
  },
  {
    label: 'Low availability',
    textCls: 'text-green-700', bgCls: 'bg-green-50', ringCls: 'ring-green-500',
    filterFn: d => d.availPercent < 80,
  },
  {
    label: 'Low specific yield',
    textCls: 'text-teal-600', bgCls: 'bg-teal-50', ringCls: 'ring-teal-500',
    filterFn: d => d.specificYield < gujaratAvgSpecificYield,
  },
  {
    label: 'High alarm rate',
    textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500',
    filterFn: d => d.alarms / d.plants > 0.5,
  },
  {
    label: 'High offline rate',
    textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500',
    filterFn: d => (1 - d.availPercent / 100) > 0.05,
  },
];

const tileCounts = TILES.map(t => gujaratDistrictAnalyser.filter(t.filterFn).length);

export default function StateDistrictAnalyser() {
  const [activeTile, setActiveTile]         = useState<number | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);

  const scope = selectedDistrict ? 'discom' : 'district';

  const visibleDistricts = useMemo(() => {
    if (activeTile === null) return gujaratDistrictAnalyser;
    return gujaratDistrictAnalyser.filter(TILES[activeTile].filterFn);
  }, [activeTile]);

  const exportCSV = () => {
    const rows = scope === 'district'
      ? ['District,Plants,MWp,MWh Today,Sp.Yield,PR%,Avail%,CUF%,MoM%,Alarms',
         ...visibleDistricts.map(d =>
           `${d.name},${d.plants},${d.capacityMWp},${d.generationMWh},${d.specificYield},${d.prPercent},${d.availPercent},${d.cufPercent},${d.momTrend},${d.alarms}`)]
      : ['DISCOM,Plants,GWp,GWh Today,Sp.Yield,PR%,Avail%,CUF%,Alarms',
         ...gujaratStateDiscoms.map(d =>
           `${d.name},${d.plants},${d.capacityGWp},${d.generationGWh},${d.specificYield},${d.prPercent},${d.availPercent},${d.cufPercent},${d.alarms}`)];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'district-analyser.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── Triage tiles (6) ── */}
      <div className="grid grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const isActive = activeTile === i;
          return (
            <div
              key={t.label}
              onClick={() => setActiveTile(prev => prev === i ? null : i)}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{tileCounts[i]}</p>
              <p className="text-xs text-text-tertiary mt-1">districts</p>
            </div>
          );
        })}
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-3">
        <select
          value={selectedDistrict ?? ''}
          onChange={e => { setSelectedDistrict(e.target.value || null); setActiveTile(null); }}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
        >
          <option value="">All Districts</option>
          {gujaratDistrictAnalyser.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        {activeTile !== null && (
          <button
            onClick={() => setActiveTile(null)}
            className="text-xs text-accent hover:underline px-2 py-1 border border-accent/30 rounded-input"
          >
            Clear filter
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* ── Breadcrumb ── */}
      {scope === 'discom' && (
        <div className="flex items-center gap-1 text-xs text-text-secondary">
          <button onClick={() => setSelectedDistrict(null)} className="text-accent hover:underline font-medium">Gujarat</button>
          <ChevronRight size={11} className="text-text-tertiary" />
          <span className="font-medium text-text-primary">{selectedDistrict}</span>
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold text-text-primary">
            {scope === 'district'
              ? `${visibleDistricts.length} district${visibleDistricts.length !== 1 ? 's' : ''}${activeTile !== null ? ` · ${TILES[activeTile].label}` : ''}`
              : `DISCOMs — ${selectedDistrict}`
            }
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Name</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Capacity</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Gen today</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Sp. yield</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">PR %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Avail %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">CUF %</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">MoM %</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Active / Offline</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
              </tr>
            </thead>
            <tbody>
              {scope === 'district' ? (
                visibleDistricts.map(d => {
                  const activePl  = Math.round(d.plants * d.availPercent / 100);
                  const offlinePl = d.plants - activePl;
                  return (
                    <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                      <td className="py-2.5 px-4">
                        <button
                          onClick={() => setSelectedDistrict(d.name)}
                          className="font-medium text-accent hover:underline text-left"
                        >
                          {d.name}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.capacityMWp.toFixed(0)} MWp</td>
                      <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationMWh.toLocaleString()} MWh</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.specificYield.toFixed(2)} kWh/kWp</td>
                      <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right ${d.availPercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.availPercent.toFixed(0)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.cufPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${d.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {d.momTrend >= 0 ? '↑' : '↓'}{Math.abs(d.momTrend).toFixed(1)}%
                      </td>
                      <td className="py-2.5 px-3 text-xs text-text-secondary">
                        {activePl.toLocaleString()} active · <span className={offlinePl > d.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl.toLocaleString()} offline</span>
                      </td>
                      <td className={`py-2.5 px-3 text-right ${d.alarms > 15 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.alarms}</td>
                    </tr>
                  );
                })
              ) : (
                // DISCOM view (proxy for any district)
                gujaratStateDiscoms.map(d => {
                  const activePl  = Math.round(d.plants * d.availPercent / 100);
                  const offlinePl = d.plants - activePl;
                  return (
                    <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                      <td className="py-2.5 px-4 font-medium text-text-primary">{d.name}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.capacityGWp.toFixed(2)} GWp</td>
                      <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationGWh.toFixed(2)} GWh</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.specificYield.toFixed(2)} kWh/kWp</td>
                      <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right ${d.availPercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.availPercent.toFixed(0)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.cufPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${d.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {d.momTrend >= 0 ? '↑' : '↓'}{Math.abs(d.momTrend).toFixed(1)}%
                      </td>
                      <td className="py-2.5 px-3 text-xs text-text-secondary">
                        {activePl.toLocaleString()} active · <span className={offlinePl > d.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl.toLocaleString()} offline</span>
                      </td>
                      <td className={`py-2.5 px-3 text-right ${d.alarms > 40 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.alarms}</td>
                    </tr>
                  );
                })
              )}
              {scope === 'district' && visibleDistricts.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-xs text-text-tertiary">
                    No districts match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
